import React, { useState } from "react";
import { FaRegCreditCard, FaCalendarAlt, FaClock, FaTimes } from "react-icons/fa";
import { loadStripe } from "@stripe/stripe-js";
import { createPaymentIntent, cancelSubscription, reactivateSubscription } from "../services/LoginService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Initialise Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC);

const UserAbonnement = ({ user, token }) => {
    const [openModal, setOpenModal] = useState(false);
    const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
    const [canceling, setCanceling] = useState(false);
    const [reactivating, setReactivating] = useState(false);

    if (!user) return null;

    const currentPlan = user.current_plan;
    const prochain_paiement = user.prochain_paiement ? new Date(user.prochain_paiement) : null;
    const dernierPaiement = user.paiements.length > 0 ? user.paiements[0] : null;

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return "";
        const options = { day: "2-digit", month: "short", year: "numeric" };
        const datePart = d.toLocaleDateString("fr-FR", options);
        const timePart = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
        return `${datePart} à ${timePart}`;
    };

    const formatMoney = (value) => (value ? `${value} ${user.devise}` : "");

    const statutColorsTable = {
        en_attente: "text-yellow-500",
        reussi: "text-green-500",
        echoue: "text-red-500",
        rembourse: "text-blue-500",
    };

    const handlePayNow = async (paiement) => {
        try {
            const stripe = await stripePromise;
            const billing = paiement.billing || (user.plan_fin && new Date(user.plan_fin) > new Date() ? "yearly" : "monthly");
            const res = await createPaymentIntent(parseFloat(paiement.montant), user.current_plan, billing, token);

            if (!res.session_id) throw new Error("Session Stripe introuvable");

            localStorage.setItem("stripe_payment_intent_id", res.session_id);

            const { error } = await stripe.redirectToCheckout({ sessionId: res.session_id });
            if (error) toast.error("Erreur Stripe: " + error.message);
        } catch (err) {
            console.error(err);
            toast.error("Erreur paiement : " + err.message);
        }
    };

    const handleCancelConfirm = async () => {
        setCanceling(true);
        try {
            const res = await cancelSubscription(token);
            if (res.success) {
                toast.success(res.message);
                window.location.reload(); // recharge la page
            } else {
                toast.error("Erreur: " + res.message);
            }
        } catch (err) {
            console.error(err);
            toast.error("Erreur annulation: " + err.message);
        } finally {
            setCanceling(false);
            setConfirmCancelOpen(false);
        }
    };

    const handleReactivate = async () => {
        setReactivating(true);
        try {
            const res = await reactivateSubscription(token);
            if (res.success) {
                toast.success("Abonnement réactivé avec succès !");
                window.location.reload();
            } else {
                toast.error("Erreur: " + res.message);
            }
        } catch (err) {
            console.error(err);
            toast.error("Erreur réactivation: " + err.message);
        } finally {
            setReactivating(false);
        }
    };

    return (
        <div className="flex flex-col lg:gap-6 gap-1 w-full lg:mt-8 lg:mb-10 ">

            <div className="w-full mt-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Bloc 1 : Abonnement actuel */}
                    <div className="bg-white p-4 shadow rounded-lg flex flex-col items-center gap-2">
                        <FaRegCreditCard className="text-2xl text-color1" />
                        <h3 className="font-semibold text-lg text-color6 text-center">Abonnement actuel</h3>
                        <p className={`text-sm font-bold capitalize rounded-lg px-3 py-1 ${user.abonnement_annule ? "bg-red-500 text-white" : "bg-color1 text-color2"}`}>
                            {currentPlan}
                        </p>

                        {!user.abonnement_annule ? (
                            <button
                                className={`mt-2 px-3 py-1 text-sm rounded text-white ${canceling ? "bg-gray-400 cursor-not-allowed" : "bg-red-500 hover:bg-red-600"}`}
                                onClick={() => setConfirmCancelOpen(true)}
                                disabled={canceling}
                            >
                                {canceling ? "Annulation..." : "Annuler l’abonnement"}
                            </button>
                        ) : (
                            <button
                                onClick={handleReactivate}
                                className={`mt-2 px-3 py-1 text-sm rounded-md text-white bg-green-600 hover:bg-green-700 ${reactivating ? "opacity-50 pointer-events-none" : ""}`}
                            >
                                {reactivating ? "Activation..." : "Réactiver l’abonnement"}
                            </button>
                        )}
                    </div>

                    {/* Bloc 2 : Dernier paiement */}
                    <div className="bg-white p-4 shadow rounded-lg flex flex-col items-center gap-2">
                        <FaCalendarAlt className="text-2xl text-color1" />
                        <h3 className="font-semibold text-lg text-color6 text-center">Dernier paiement</h3>
                        {dernierPaiement ? (
                            <>
                                <p className="text-sm">{formatDate(dernierPaiement.date_paiement)}</p>
                                <p className={`px-2 py-1 rounded-full text-sm font-semibold ${statutColorsTable[dernierPaiement.statut]}`}>
                                    {dernierPaiement.statut}
                                </p>
                                <p className="text-base font-medium">{formatMoney(dernierPaiement.montant)}</p>
                                {dernierPaiement.statut === "en_attente" && (
                                    <button
                                        onClick={() => handlePayNow(dernierPaiement)}
                                        className="mt-2 px-4 py-1 bg-color3 text-white rounded hover:bg-color3/80"
                                    >
                                        Payer maintenant
                                    </button>
                                )}
                            </>
                        ) : (
                            <p className="text-sm">Aucun paiement</p>
                        )}
                    </div>

                    {/* Bloc 3 : Prochain paiement */}
                    <div className="bg-white p-4 shadow rounded-lg flex flex-col items-center gap-2">
                        <FaClock className="text-2xl text-color1" />
                        <h3 className="font-semibold text-lg text-center">Prochain paiement</h3>
                        {prochain_paiement ? (
                            <p className="text-center text-sm text-color5 font-bold">{formatDate(prochain_paiement)}</p>
                        ) : user.abonnement_annule && user.plan_fin ? (
                            <p className="text-sm text-red-600 font-bold">Actif jusqu’au {formatDate(user.plan_fin)}</p>
                        ) : (
                            <p className="text-sm">Aucun paiement prévu</p>
                        )}
                        <button
                            className="mt-2 px-3 py-1 text-sm bg-color3 text-white rounded hover:bg-color3/80"
                            onClick={() => setOpenModal(true)}
                        >
                            Historique des paiements
                        </button>
                    </div>
                </div>
            </div>

            {/* Modal paiements */}
            {openModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-white rounded-lg w-11/12 md:w-3/4 p-6 relative shadow-lg">
                        <button className="absolute top-3 right-3 text-gray-500 hover:text-gray-800" onClick={() => setOpenModal(false)}>
                            <FaTimes />
                        </button>
                        <h3 className="font-semibold mb-3 text-xl">Historique des paiements</h3>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-center border-collapse">
                                <thead className="bg-color1 text-white uppercase text-xs font-semibold">
                                    <tr>
                                        <th className="px-3 py-2">Transaction ID</th>
                                        <th className="px-3 py-2">Statut</th>
                                        <th className="px-3 py-2">Date de paiement</th>
                                        <th className="px-3 py-2">Montant</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {user.paiements.length > 0 ? (
                                        user.paiements.map((p) => (
                                            <tr key={p.id} className="border-b hover:bg-gray-50">
                                                <td className="px-2 py-1">{p.fournisseur_paiement_id || "—"}</td>
                                                <td className="px-2 py-1">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statutColorsTable[p.statut]}`}>
                                                        {p.statut}
                                                    </span>
                                                </td>
                                                <td className="px-2 py-1">{formatDate(p.date_paiement)}</td>
                                                <td className="px-2 py-1">{formatMoney(p.montant)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="py-2 text-sm">Aucun paiement</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation annulation */}
            {confirmCancelOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-80">
                        <h3 className="text-lg font-semibold mb-4 text-center">Annuler l’abonnement</h3>
                        <p className="mb-6 text-center">
                            Voulez-vous vraiment annuler votre abonnement ?
                        </p>
                        <div className="flex justify-between gap-4">
                            <button
                                onClick={() => setConfirmCancelOpen(false)}
                                className="flex-1 px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
                                disabled={canceling}
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleCancelConfirm}
                                className={`flex-1 px-4 py-2 rounded-md text-white ${canceling ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"}`}
                                disabled={canceling}
                            >
                                {canceling ? "Annulation..." : "Confirmer"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserAbonnement;
